import { getResponseOptions } from './getResponseOptions';
import { tracking } from '../strings.locstring.json';
import { isConnectedAccount } from 'owa-accounts-store';
import {
    ExtensibilityModeEnum,
    CustomizeActionStatusEnum,
    CustomizeActionPlacementEnum,
} from 'owa-addins-types';
import { getCommandBarAddins } from 'owa-calendar-addins';
import { getCalendarEntryByFolderId } from 'owa-calendar-cache';
import { getCalendarItemCategoryMenuFilterableItem } from 'owa-calendar-categories-menu';
import type { FullComposeViewState } from 'owa-calendar-compose-types';
import SidePaneType from 'owa-calendar-sidepane/lib/schema/SidePaneType';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import type ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { ControlIcons } from 'owa-control-icons';
import { DisplayOption, FilterableMenuItem, FilterableMenuItemArgs } from 'owa-filterable-menu';
import { chosenShowAsOptionText, getShowAsMenuItems } from 'owa-freebusy-menu';
import loc, { format } from 'owa-localize';
import { saveButtonLabel } from 'owa-locstrings/lib/strings/savebuttonlabel.locstring.json';
import { sendButtonLabel } from 'owa-locstrings/lib/strings/sendbuttonlabel.locstring.json';
import { ReplyAllMenuItem } from 'owa-locstrings/lib/strings/replyallmenuitem.locstring.json';
import {
    discardButtonLabel,
    openSchedulingAssistantTitle,
    showAsCommandBarAriaLabel,
} from './getComposeCommandBarItems.locstring.json';
import {
    canEditCategories,
    canReply,
    hasAttendees,
    isEventAMeeting,
    isEventInstanceOfSeries,
} from 'owa-calendar-event-capabilities';
import {
    onCategoriesChanged,
    onSensitivityToggled,
    presentDiscardClosureConfirmation,
    updateFreeBusy,
    updateSidePaneType,
    verifyEventThenSave,
    deleteEvent,
    closeComposeForm,
    openComposeSchedulingAssistant,
    openNewFormToDuplicate,
} from 'owa-calendar-compose-form-actions';
import updateContentToViewState from 'owa-editor/lib/utils/updateContentToViewState';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import { lazyMountAndShowFullOptions } from 'owa-options-view';
import { customizeActionsText } from 'owa-locstrings/lib/strings/customizeActionsText.locstring.json';
import { isFeatureEnabled } from 'owa-feature-flags';
import { replyToMeeting } from 'owa-reply-to-meeting';
import { getDeleteButton } from 'owa-calendar-forms-common/lib/utils/command-bar/getDeleteButton';
import { getCancelButton } from 'owa-calendar-forms-common/lib/utils/command-bar/getCancelButton';
import {
    isInPopOutWindow,
    isInProjectionPopOutWindow,
} from 'owa-calendar-compose-form-store/lib/selectors/popoutSelectors';
import { duplicateEventButtonLabel } from 'owa-locstrings/lib/strings/duplicateeventbuttonlabel.locstring.json';
import isNewEvent from 'owa-calendar-compose-form-store/lib/selectors/isNewEvent';
import {
    isSaveButtonInlineEnabled,
    isRemoveDiscardInEditEnabled,
    isReplyAllInEditEnabled,
} from '../utils/getCommandBarUpdatesSDF';
import {
    lazyHighlightCustomizeActionButton,
    lazyLogCustomizeActionButtonAction,
} from 'owa-addins-core';
import type EventScope from 'owa-service/lib/contract/EventScope';
import type BusyType from 'owa-service/lib/contract/BusyType';
import { isSchedulingAssistantEnabled } from 'owa-calendar-scheduling-assistant';

export default function getComposeCommandBarItems(
    item: CalendarEvent,
    originalItem: CalendarEvent,
    fullComposeViewState: FullComposeViewState,
    formId: string,
    onDismissContextMenu: () => void,
    calendarColorScheme: ColorScheme,
    disabled: boolean,
    targetWindow: Window
): FilterableMenuItem[] {
    const canSendBehavior = () =>
        isEventAMeeting(item) &&
        isHostAppFeatureEnabled('saveInFullCompose') &&
        !isSaveButtonInlineEnabled();
    const canSaveBehavior = () =>
        !isEventAMeeting(item) &&
        isHostAppFeatureEnabled('saveInFullCompose') &&
        !isSaveButtonInlineEnabled();
    const canDiscardBehavior = () =>
        isHostAppFeatureEnabled('discardInFullCompose') && !isRemoveDiscardInEditEnabled();
    const shouldShowTrackingBehavior = () =>
        item.MeetingRequestWasSent && hasAttendees(originalItem) && hasAttendees(item);
    const onSaveOrSend = () => {
        updateContentToViewState(fullComposeViewState?.bodyViewState);
        verifyEventThenSave(formId, targetWindow);
    };
    const sensitivityToggled = () => {
        onSensitivityToggled(formId);
    };

    const canReplyAllBehavior = () => isReplyAllInEditEnabled() && canReply(item);
    const onReplyAll = () => replyToMeeting(item, true /* isReplyAll */, 'EditForm');
    const isEventAnInstanceOfSeries: boolean = isEventInstanceOfSeries(item);

    const smtpOfCalendarOwner =
        item != null && item.ParentFolderId != null
            ? item.ParentFolderId.mailboxInfo.userIdentity
            : originalItem != null && originalItem.ParentFolderId != null
            ? originalItem.ParentFolderId.mailboxInfo.userIdentity
            : undefined;

    const isAConnectedAccount: boolean = isConnectedAccount(smtpOfCalendarOwner);
    const enableAddins = !isInProjectionPopOutWindow(formId);

    // We need to have an additional check for connected account because the WebSessionType is not set to the correct value
    // for connected accounts
    const shouldShowSchedulingAssistant: boolean = isSchedulingAssistantEnabled(item, originalItem);
    const shouldShowDuplicateButton = () =>
        isFeatureEnabled('mon-cal-DuplicateInEditForm') && !isNewEvent(formId);
    const presentClosureConfirmation = () =>
        presentDiscardClosureConfirmation(formId, targetWindow);
    const categoriesChangedHandler = newCategoriesList =>
        onCategoriesChanged(newCategoriesList, formId);
    const onDeleteEvent = (
        eventScope: EventScope,
        isInPopOutWindow: boolean,
        event: CalendarEvent
    ) => {
        deleteEvent(eventScope, isInPopOutWindow, event, formId, targetWindow);
    };
    const onUpdateFreeBusy = (busyType: BusyType) => {
        updateFreeBusy(busyType, formId);
    };

    let items = [
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(sendButtonLabel),
            [FilterableMenuItemArgs.icon]: ControlIcons.Send,
            [FilterableMenuItemArgs.onClick]: onSaveOrSend,
            [FilterableMenuItemArgs.shouldShow]: canSendBehavior,
            [FilterableMenuItemArgs.disabled]: disabled,
        }),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(saveButtonLabel),
            [FilterableMenuItemArgs.icon]: ControlIcons.Save,
            [FilterableMenuItemArgs.onClick]: onSaveOrSend,
            [FilterableMenuItemArgs.shouldShow]: canSaveBehavior,
            [FilterableMenuItemArgs.disabled]: disabled,
        }),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(discardButtonLabel),
            [FilterableMenuItemArgs.icon]: ControlIcons.Delete,
            [FilterableMenuItemArgs.onClick]: presentClosureConfirmation,
            [FilterableMenuItemArgs.shouldShow]: canDiscardBehavior,
            [FilterableMenuItemArgs.key]: 'Discard',
            [FilterableMenuItemArgs.disabled]: disabled,
        }),
        ...(isFeatureEnabled('mon-cal-addDeleteOrCancelInEdit')
            ? [
                  getDeleteButton(
                      item,
                      'EditForm',
                      true /*isFullForm*/,
                      isInPopOutWindow(formId),
                      onDeleteEvent
                  ),
              ]
            : []),
        ...(isFeatureEnabled('mon-cal-addDeleteOrCancelInEdit')
            ? [
                  getCancelButton(item, 'EditForm', () => {
                      closeComposeForm(
                          formId,
                          item.IsMeeting,
                          true /* shouldResetComposeStore */,
                          false /* shouldClosePopOutWindow */
                      );
                  }),
              ]
            : []),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(ReplyAllMenuItem),
            [FilterableMenuItemArgs.icon]: ControlIcons.ReplyAll,
            [FilterableMenuItemArgs.onClick]: onReplyAll,
            [FilterableMenuItemArgs.shouldShow]: canReplyAllBehavior,
            [FilterableMenuItemArgs.key]: 'ReplyAll',
            [FilterableMenuItemArgs.disabled]: disabled,
        }),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(openSchedulingAssistantTitle),
            [FilterableMenuItemArgs.icon]: ControlIcons.ScheduleEventAction,
            [FilterableMenuItemArgs.onClick]: () => {
                openComposeSchedulingAssistant(formId);
            },
            [FilterableMenuItemArgs.shouldShow]: () => shouldShowSchedulingAssistant,
            [FilterableMenuItemArgs.disabled]: disabled,
            [FilterableMenuItemArgs.key]: 'SchedulingAssistant',
        }),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(tracking),
            [FilterableMenuItemArgs.icon]: ControlIcons.PeopleAlert,
            [FilterableMenuItemArgs.onClick]: () =>
                fullComposeViewState.currentSidePaneType == SidePaneType.Tracking
                    ? updateSidePaneType(formId, SidePaneType.None)
                    : updateSidePaneType(formId, SidePaneType.Tracking),
            [FilterableMenuItemArgs.shouldShow]: shouldShowTrackingBehavior,
            [FilterableMenuItemArgs.disabled]: disabled,
        }),
        getResponseOptions(item, disabled, onDismissContextMenu, formId, isReplyAllInEditEnabled()),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: chosenShowAsOptionText(item),
            [FilterableMenuItemArgs.icon]: ControlIcons.ShowTimeAs,
            [FilterableMenuItemArgs.displayOption]: DisplayOption.Left,
            [FilterableMenuItemArgs.disabled]: disabled,
            [FilterableMenuItemArgs.menuContent]: {
                items: getShowAsMenuItems(
                    item,
                    onUpdateFreeBusy /* onClick */,
                    calendarColorScheme,
                    sensitivityToggled,
                    !isEventAnInstanceOfSeries /* showPrivateOption */
                ),
                onDismiss: onDismissContextMenu,
            },
            [FilterableMenuItemArgs.key]: 'ShowAs',
            [FilterableMenuItemArgs.ariaLabel]: format(
                loc(showAsCommandBarAriaLabel),
                chosenShowAsOptionText(item)
            ),
        }),
        getCalendarItemCategoryMenuFilterableItem(
            getCalendarEntryByFolderId(item.ParentFolderId.Id),
            item,
            'CommandBar',
            categoriesChangedHandler,
            categoriesChangedHandler,
            categoriesChangedHandler,
            !isEventAnInstanceOfSeries && canEditCategories(item),
            !isAConnectedAccount && isHostAppFeatureEnabled('settings'), // show manage categories
            true, // show search
            onDismissContextMenu,
            ControlIcons.Tag,
            disabled
        ),
        getResponseOptions(
            item,
            disabled,
            onDismissContextMenu,
            formId,
            !isReplyAllInEditEnabled()
        ),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(duplicateEventButtonLabel),
            [FilterableMenuItemArgs.icon]: ControlIcons.Copy,
            [FilterableMenuItemArgs.onClick]: () => {
                openNewFormToDuplicate(formId, targetWindow);
            },
            [FilterableMenuItemArgs.shouldShow]: shouldShowDuplicateButton,
            [FilterableMenuItemArgs.displayOption]: DisplayOption.Overflow,
        }),
        // Disable addins until they are supported for calendar projection
        // VSO# https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/127874
        ...(item.ParentFolderId && isHostAppFeatureEnabled('addIns') && enableAddins
            ? getCommandBarAddins(
                  ExtensibilityModeEnum.AppointmentOrganizer,
                  item,
                  formId,
                  disabled
              )
            : []),
        // Customize action button being added here
        ...(enableAddins ? [getCustomizeActionsButton()] : []),
    ];

    return items;
}

export const getCustomizeActionsButton = (): FilterableMenuItem => {
    let isCustomizeActionButtonHighlighted = false;
    let custBtnMenu: FilterableMenuItem = new FilterableMenuItem({
        [FilterableMenuItemArgs.name]: loc(customizeActionsText),
        [FilterableMenuItemArgs.onClick]: () => {
            lazyMountAndShowFullOptions.importAndExecute(
                'calendar',
                'calendarSurfaceActions',
                'calendarSurfaceAddins'
            );
            // Log 'CustomizeActionEvents' telemetry event for customize action button actions.
            lazyLogCustomizeActionButtonAction.importAndExecute(
                isCustomizeActionButtonHighlighted
                    ? CustomizeActionStatusEnum.HighlightedAndClicked
                    : CustomizeActionStatusEnum.Clicked,
                CustomizeActionPlacementEnum.OverFlowMenu
            );
        },
        [FilterableMenuItemArgs.shouldShow]: () =>
            isFeatureEnabled('addin-exp-customizedActionsButtonNavBar'),
        [FilterableMenuItemArgs.key]: 'CustomizeAction',
        [FilterableMenuItemArgs.displayOption]: DisplayOption.Overflow,
    });

    if (
        isFeatureEnabled('addin-exp-awarenessNudgeCusBtn') ||
        isFeatureEnabled('addin-exp-awarenessNudge') ||
        isFeatureEnabled('addin-exp-awarenessNudgeWaterDropNudge')
    ) {
        lazyHighlightCustomizeActionButton.importAndExecute(custBtnMenu);
        isCustomizeActionButtonHighlighted = true;
    }

    return custBtnMenu;
};
